// Libraries
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import moment from 'moment';

// MUI
import Grid from '@mui/material/Grid';

// Core
import { IFormValidator } from '../core/FormValidator';
import { ResourceTextApplication } from '../core/resources';
import auth from '../core/authorization';

// Common
import { AAddButton } from '../common/buttons';
import {
    ADialog,
    ADialogActions,
    ADialogButtonsProp,
    ADialogContent,
    ADialogTitle,
} from '../common/dialog';
import {
    ADateInput,
    AInputContainer,
    ASwitch,
    ATextarea,
    ATextInput,
    ATimeInput,
} from '../common/inputs';

// Interfaces
import { IContactFarmRow, IContactPersonRow, IContactRow } from '../interfaces/IBusinessEntities';

// Store
// API
import shortMessagesApi from '../api/shortMessageApi';

// Feature - SMS
import SearchMessageGroupDialog from './search/SearchMessageGroupDialog';
import { IMessages } from './ISms';
import { mapMessagesDataToRequest } from './SendMessageUpdateRequest';
import { SMS } from './constants/SMS';
import Recipients from '../common/communicationGroup/Recipients';

export interface SendMessageFormDialogProps {
    data: IMessages;
    onClose: (update: boolean) => void;
    mode: 'create' | 'edit' | 'read';
    validator: IFormValidator;
}

export default function SendMessageFormDialog(props: SendMessageFormDialogProps) {
    const { data, validator } = props;

    const { t } = useTranslation<ResourceTextApplication[]>([
        'AnelmaCompany',
        'AnelmaCommunication',
        'AnelmaGeneral',
        'AnelmaLayout',
        'AnelmaSMS',
        'AnelmaBulletin',
    ]);
    const { enqueueSnackbar } = useSnackbar();

    const [selectedNumbers, setSelectedNumbers] = useState<IContactRow[]>([]);
    const [receiverInput, setReceiverInput] = useState<string[]>(data.Receiver);
    const [contentInput, setContentInput] = useState<string>(data.Content);

    const [scheduleMessage, setScheduleMessage] = useState<boolean>(data.MessageType === 'ScheduledMessage');
    const [scheduleDate, setScheduleDate] = useState<moment.Moment | null>(
        data.MessageType === 'ScheduledMessage' ? moment(data.RequestedDeliveryDate + 'Z') : null
    );
    const [scheduleTime, setScheduleTime] = useState<moment.Moment | null>(
        data.MessageType === 'ScheduledMessage' ? moment(data.RequestedDeliveryDate + 'Z') : null
    );

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [recipientsOpen, setRecipientsOpen] = useState<boolean>(false);
    const [companySearchDialog, setCompanySearchDialog] = useState<boolean>(false);

    const onSend = () => {
        if (props.validator.invalid) {
            enqueueSnackbar(t('AnelmaGeneral:1030'), {
                variant: 'error',
            });
            return;
        }
        setIsLoading(true);

        //send to api
        if (props.mode === 'edit') {
            const updates = mapMessagesDataToRequest(data, editPayload);
            shortMessagesApi.updateMessage(updates.Entity.Guid, updates).then(
                (response) => {
                    if (response && response.Results.HttpStatus === 202) {
                        showMessage(t('AnelmaGeneral:1018'), 'success');
                    } else {
                        showMessage(t('AnelmaCommunication:1059'), 'error');
                    }
                    props.onClose(true);
                },
                (fail) => {
                    setIsLoading(false);
                    showMessage(t('AnelmaCommunication:1059'), 'error');
                    props.onClose(false);
                }
            );
        } else if (props.mode === 'create') {
            //if no reciever selected prevent to make API call
            if (selectedNumbers.length === 0 || !Array.isArray(selectedNumbers)) {
                setIsLoading(false);
                return showMessage(t('AnelmaSMS:1001'), 'error');
            }

            shortMessagesApi.sendDirectMessage(payload).then(
                (response) => {
                    if (response && response.Results.HttpStatus === 202) {
                        showMessage(t('AnelmaGeneral:1018'), 'success');
                    } else {
                        showMessage(t('AnelmaCommunication:1059'), 'error');
                    }

                    props.onClose(true);
                },
                (fail) => {
                    setIsLoading(false);
                    showMessage(t('AnelmaCommunication:1059'), 'error');
                    props.onClose(false);
                }
            );
        }
    };

    const addReceivers = (toAdd: IContactRow[]) => {
        toAdd = toAdd.filter(
            (c) =>
                selectedNumbers.findIndex(
                    (n) =>
                        n.EntityType === c.EntityType &&
                        n.EntityId === c.EntityId &&
                        n.ProducerNumber === c.ProducerNumber &&
                        n.PhoneNumber === c.PhoneNumber
                ) === -1
        );

        setSelectedNumbers([...selectedNumbers, ...toAdd]);
    };

    const getRequestedDeliveryDate = () => {
        let d = scheduleDate?.format('YYYY-MM-DD');
        let t = scheduleTime?.format('HH:mm:ss');
        let reqDateTime = moment(d + ' ' + t);

        if (reqDateTime?.isValid())
            return reqDateTime.utc().format('YYYY-MM-DD HH:mm:00');

        return props.mode === 'create'
            ? ''
            : moment().utc().format('YYYY-MM-DD HH:mm:00');
    };

    const resolveReceiverTxt = (recipDialogItems: IContactRow[]) => {
        return isLoading
            ? ''
            : t('AnelmaBulletin:1032', {
                  RecieverCount: recipDialogItems.length,
              });
    };

    const getReceiversAmount = () => {
        if (props.mode === 'create')
            return selectedNumbers.length.toString();

        return '1'; // because there is one row for one number
    }

    const showMessage = (message: string, variant: 'success' | 'error') => {
        enqueueSnackbar(message, {
            variant: variant,
        });
    };

    const handleScheduleSwitchChanged = () => {
        setScheduleDate(null);
        setScheduleTime(null);

        setScheduleMessage((oldIsOpen) => !oldIsOpen);
    }

    //create payload
    const payload: IMessages = {
        ...data,
        Receivers: selectedNumbers.map((i) => {
            if (i.EntityType === 'Person') {
                let person = i as IContactPersonRow;
                return {
                    EntityId: person.FarmId,
                    ReceiverId: person.EntityId,
                    ReceiverPhoneNumber: person.PhoneNumber,
                };
            }

            let company = i as IContactFarmRow;
            return {
                EntityId: company.EntityId,
                ReceiverId: '',
                ReceiverPhoneNumber: company.PhoneNumber,
            };
        }),
        Content: contentInput,
        RequestedDeliveryDate: getRequestedDeliveryDate(),
    };

    const editPayload: IMessages = {
        ...data,
        Receiver: receiverInput,
        Content: contentInput,
        RequestedDeliveryDate: getRequestedDeliveryDate(),
    };

    const footerButtons: ADialogButtonsProp = {
        left:
            selectedNumbers.length > 0 && auth.canViewAnelmaCommunicationGroups
                ? [
                      {
                          type: 'cancel',
                          onClick: () => props.onClose(false),
                      },
                      {
                          type: 'with-label',
                          label: t('AnelmaCommunication:1050'),
                          onClick: () => setRecipientsOpen(true),
                          leftSideText: resolveReceiverTxt(selectedNumbers),
                          disabled: isLoading,
                      },
                  ]
                : [
                      {
                          type: 'cancel',
                          onClick: () => props.onClose(false),
                      },
                  ],
        right: [
            {
                type: 'send',
                onClick: () => onSend(),
                disabled: props.mode === 'read' || !auth.canSendSms,
            },
        ],
    };

    return (
        <div>
            <ADialog open onClose={() => props.onClose(false)}>
                <ADialogTitle>{t('AnelmaCommunication:1062')}</ADialogTitle>
                <ADialogContent isLoading={isLoading} size='lg'>
                    <Grid container>
                        <Grid size={{ sm: 6 }}>
                            <ATextInput
                                disabled
                                id={'receiver'}
                                label={t('AnelmaCommunication:1063')}
                                validator={validator}
                                onChange={(e) => { }}
                                value={getReceiversAmount()}
                            ></ATextInput>
                            <Grid container>
                                <Grid size={{ xs: 12 }}>
                                    <AInputContainer>
                                        <>
                                            <AAddButton
                                                type='action'
                                                onClick={() => {
                                                    setCompanySearchDialog(!companySearchDialog);
                                                }}
                                                disabled={props.mode !== 'create'}
                                            >
                                                {t('AnelmaCommunication:1067')}
                                            </AAddButton>
                                        </>
                                    </AInputContainer>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid size={{ sm: 6 }}>
                            <ASwitch
                                id={'schedule'}
                                label={t('AnelmaCommunication:1064')}
                                checked={scheduleMessage}
                                onChange={handleScheduleSwitchChanged}
                                validator={props.validator}
                                disabled={props.mode === 'read'}
                            ></ASwitch>

                            <ADateInput
                                id='valid-from-date'
                                label={t('AnelmaCommunication:1065')}
                                onChange={(d) => {
                                    setScheduleDate(moment(d));
                                }}
                                validator={validator}
                                value={scheduleDate?.toDate() || null}
                                disabled={!scheduleMessage || props.mode === 'read'}
                            />

                            <ATimeInput
                                id='valid-from-time'
                                label={t('AnelmaCommunication:1066')}
                                onChange={(t) => {
                                    setScheduleTime(moment(t));
                                }}
                                validator={validator}
                                value={scheduleTime?.toDate() || null}
                                disabled={!scheduleMessage || props.mode === 'read'}
                            />
                        </Grid>
                    </Grid>

                    <Grid size={{ sm: 12 }}>
                        <ATextarea
                            id={'content'}
                            label={t('AnelmaCommunication:1069')}
                            minLength={SMS.ContentMinimumLength}
                            maxLength={SMS.ContentMaximumLength}
                            validator={validator}
                            required
                            onChange={(e) => {
                                setContentInput(e);
                            }}
                            value={contentInput}
                            disabled={props.mode === 'read'}
                        />
                    </Grid>
                </ADialogContent>
                <ADialogActions buttons={footerButtons} isLoading={isLoading} />
            </ADialog>

            {companySearchDialog && (
                <SearchMessageGroupDialog
                    validator={validator}
                    onSelect={(items) => {
                        addReceivers(items);
                    }}
                    onClose={() => {
                        setCompanySearchDialog(!companySearchDialog);
                    }}
                    showPhoneNumbers
                />
            )}

            {recipientsOpen && (
                <Recipients
                    onClose={() => setRecipientsOpen(false)}
                    items={selectedNumbers}
                    titleParameter={'AnelmaBulletin:1032'}
                    showPhoneNumbers
                />
            )}
        </div>
    );
}
